import { defineComponent, type PropType } from 'vue'

import type {
	BadgeType,
	CABadgeAppearance,
	CABadgeStyle,
} from '../interfaces'
import CAIcon from './shared/CAIcon'

/**
 * A badge.
 *
 * @component CABadge
 *
 * @prop {CABadgeAppearance} [appearance='tag'] - The appearance of the badge.
 * @prop {CABadgeStyle} [badgeStyle='simple'] - The style of the badge.
 * @prop {string} [dataTest='ca-badge'] - The data-buttonDataTest attribute for the badge component.
 * @prop {boolean} [disableTitleMutation=true] - Whether the title capitalization should between overwritten.
 * @prop {('large' | 'medium' | 'small')} [size='small'] - The size of the badge.
 * @prop {string} [text=''] - The text within the badge.
 * @prop {string} [tooltip=''] - The tooltip to show when hovering over the badge.
 * @prop {boolean} [truncated=false] - Whether the badge text should be truncated
 * @prop {BadgeType} [type='gray'] - The type of badge determines the color of the badge.
 *
 * @emit close - Emits when the close icon is clicked.
 *
 * @slot default - The default slot for the badge content.
 */
export default defineComponent({
	name: 'CABadge',

	props: {
		/**
		 * The appearance of the badge determines what kind of formatting will be applied to your component.
		 *
		 * @default 'tag'
		 */
		appearance: { default: 'tag', type: String as PropType<CABadgeAppearance> },
		/**
		 * We have three accepted styles within our design system.
		 *
		 * @default 'simple'
		 * @type {CABadgeStyle}
		 * @enum {('simple' | 'filled' | 'outlined')}
		 */
		badgeStyle: { default: 'simple', type: String as PropType<CABadgeStyle> },
		/**
		 * The data-test attribute for the badge component.
		 */
		dataTest: { default: 'ca-badge', type: String },
		/**
		 * Whether the title capitalization should be overwritten.
		 *
		 * @default true
		 */
		disableTitleMutation: { default: true, type: Boolean },
		/**
		 * This size of the badge.
		 *
		 * @default 'small'
		 * @type {('large' | 'medium' | 'small')}
		 * @enum {('large' | 'medium' | 'small')}
		 */
		size: { default: 'small', type: String as PropType<'large' | 'medium' | 'small'> },
		/**
		 * This text within the badge.
		 */
		text: { default: '', type: String },
		/**
		 * This controls whether to show a tooltip over a badge. Most cases this is for really long badge name that are truncated.
		 */
		tooltip: { default: '', type: String },
		truncated: { default: false, type: Boolean },
		/**
		 * The type of badge determines the color of the badge.
		 *
		 * @default 'gray'
		 */
		type: { default: 'gray', type: String as PropType<BadgeType> },
		/**
		 * The URL to navigate to when the badge is clicked.
		 */
		url: { default: '', type: String },
	},

	emits: [
		'click',
		'close',
	],

	computed: {
		badgeIcon() {
			return this.type === 'success' ? 'SVGCheckCircleFilled' :
				this.type === 'danger' || this.type === 'error' || this.type === 'warning' ?
					'SVGExclamationMarkCircleFilled' : 'SVGDot'
		},

		computedTooltip() {
			return this.tooltipIsEnabled
				? this.tooltip ?? this.text ?? ''
				: ''
		},

		tooltipIsEnabled() {
			return this.tooltip !== null || this.truncatingEnabled
		},

		truncatingEnabled() {
			return this.text !== null && this.truncated && this.text?.length >= 13 /* eslint-disable-line no-magic-numbers */
		},
	},

	methods: {
		handleClick() {
			if (this.url) {
				window.location.href = this.url
			}
		},
	},

	render() {
		return (
			<span
				class={[
					'ca-badge',
					`ca-badge__${this.type}`,
					`ca-badge__${this.appearance}`,
					`ca-badge__${this.badgeStyle}`,
					!!this.size ? `ca-badge__${this.size}` : '',
					{
						'ca-badge__default': this.type === 'gray',
						'ca-badge__info': this.type === 'info',
					},
				]}
				data-test={this.dataTest}
				v-tooltip={this.computedTooltip}
				onClick={() => this.$emit('click')}
			>
				{this.appearance === 'date' &&
					<CAIcon icon="SVGClock" svg-class="ca-badge__date" />
				}

				{this.appearance === 'alert' &&
					<CAIcon icon={this.badgeIcon} svg-class="ca-badge__alert" />
				}

				<span
					class={{
						'ca-badge__has-tooltip': this.computedTooltip.length >= 19 && this.text, /* eslint-disable-line no-magic-numbers */
						'ca-badge__has-url': !!this.url,
						'overflow-control': this.truncated && this.text,
					}}
				>
					{this.url ?
						<a
							class={{ 'disable-title-mutation': this.disableTitleMutation }}
							onClick={this.handleClick}
						>
							{this.text}
							{this.$slots.default?.()}
						</a>

						:

						<span class={{ 'disable-title-mutation': this.disableTitleMutation }}>
							{this.text}
							{this.$slots.default?.()}
						</span>
					}
				</span>

				{this.appearance === 'close' &&
					<CAIcon
						class="ca-badge__close"
						data-test="ca-badge-close"
						icon="SVGClose"
						onClick={() => this.$emit('close')}
					/>
				}
			</span>
		)
	},
})
